import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Button,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  KeyboardArrowDown,
  KeyboardArrowRight,
  ExpandMore,
  SwapHoriz,
  OpenInNew,
  Close as CloseIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { CheckEngineGrid, RiskBadge, SIGNAL_METADATA, SIGNAL_ORDER, SIGNAL_CATEGORY, OwnerGroupCaseActionSection } from '../components/understaffing';
import OwnerGroupCostReportTab from '../components/understaffing/OwnerGroupCostReportTab';
import api from '../api/axios';

/* ───────────────── types ───────────────── */

interface GroupInfo {
  group_id: string;
  group_name: string;
  lead_type_owner: string;
  num_facilities: number;
  num_states: number;
  state_list: string[];
  ccn_list: string[];
  co_owner_names?: string[];
  co_owner_types?: string[];
}

interface StateRollup {
  state: string;
  num_facilities: number;
  total_ant_cost: number | null;
  total_ant_cost_no_credit: number | null;
  hybrid_mds_cost: number | null;
  hybrid_mds_cost_no_credit: number | null;
  total_rp_paid: number | null;
  total_harm_citations: number | null;
  total_citations: number | null;
  avg_overall_rating: number | null;
  avg_staffing_rating: number | null;
  total_fine_amount: number | null;
  avg_acuity_gap?: number | null;
  pct_ant_days_any_below?: number | null;
  pct_days_meeting_state_min?: number | null;
  facilities?: any[];
}

interface FacilityYear {
  ccn: string;
  year: number;
  facility_name: string;
  mds_understaffing_cost: number | null;
  mds_overstaffing_credit: number | null;
  mds_understaffing_cost_no_credit: number | null;
  rp_total_paid: number | null;
  rp_disallowed: number | null;
  rp_plus_mds: number | null;
  avg_census: number | null;
  risk_score: number | null;
  top_risk_factors: any;
  no_cost_report_data: boolean;
  ant_pct_acuity_staffed_total?: number | null;
  pct_ant_days_any_below?: number | null;
  ant_days_any_below?: number | null;
  days_with_mds_and_pbj_data?: number | null;
  ant_pbj_rn_hrs?: number | null;
  ant_pbj_lpn_hrs?: number | null;
  ant_pbj_cna_hrs?: number | null;
  ant_expected_rn_hrs_hours?: number | null;
  ant_expected_lpn_hrs_hours?: number | null;
  ant_expected_cna_hrs_hours?: number | null;
  hybrid_mds_understaffing_cost?: number | null;
  hybrid_mds_understaffing_cost_no_credit?: number | null;
  hybrid_pct_acuity_staffed_total?: number | null;
  hybrid_pct_days_any_below?: number | null;
  hybrid_days_any_below?: number | null;
  hybrid_days_with_mds_and_pbj_data?: number | null;
}

interface GroupHistoryPeriod {
  group_id: string;
  group_name: string;
  start_month: string;
  end_month: string;
  is_this_group: boolean;
}

interface OwnerGroupData {
  group: GroupInfo;
  members: any[];
  states: StateRollup[];
  facility_years?: FacilityYear[];
  facility_addresses?: Record<string, string>;
  facility_totals?: Record<string, { mds_understaffing_cost: number; mds_understaffing_cost_no_credit: number; hybrid_mds_cost: number; hybrid_mds_cost_no_credit: number }>;
  ownership_dates?: Record<string, string>;
  current_ownership?: Record<string, boolean>;
  ownership_end_dates?: Record<string, string>;
  chow_exclusions?: Record<string, any[]>;
  facility_group_history?: Record<string, GroupHistoryPeriod[]>;
}

interface PeriodOwner {
  owner_name: string;
  type: string;
  percentage: number | null;
}

interface OwnershipPeriod {
  enrollment_id: string;
  facility_name: string;
  start_month: string;
  end_month: string;
  is_current: boolean;
  direct_owners: PeriodOwner[];
  indirect_owners: PeriodOwner[];
  managerial_control: PeriodOwner[];
}

interface ChowEvent {
  effective_date: string;
  seller_name: string;
  buyer_name: string;
  chow_type: string;
  chow_type_text: string;
}

interface OwnershipData {
  owners: any[];
  other_facilities: any[];
  chow_events?: ChowEvent[];
  ownership_periods?: OwnershipPeriod[];
}

/* ───────────────── helpers ───────────────── */

const formatMoney = (val: number | null | undefined): string => {
  if (val == null) return '-';
  const prefix = val < 0 ? '-$' : '$';
  const abs = Math.abs(val);
  if (abs >= 1e9) return `${prefix}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${prefix}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${prefix}${(abs / 1e3).toFixed(0)}K`;
  return `${prefix}${abs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatMoneyFull = (val: number | null | undefined): string => {
  if (val == null) return '-';
  const prefix = val < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (val: number | null | undefined, decimals = 0): string => {
  if (val == null) return '-';
  return val.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const getRiskColor = (score: number): string => {
  if (score >= 81) return '#d32f2f';
  if (score >= 61) return '#e65100';
  if (score >= 41) return '#c17900';
  if (score >= 21) return '#558b2f';
  return '#2e7d32';
};

const getRiskLabel = (score: number): string => {
  if (score >= 81) return 'Critical';
  if (score >= 61) return 'High';
  if (score >= 41) return 'Elevated';
  if (score >= 21) return 'Moderate';
  return 'Low';
};

const OWNER_RISK_FACTORS = [
  { key: 'factor1_score', label: 'Total Acuity Gap', weight: 20, description: 'How far below acuity-based staffing needs.' },
  { key: 'factor2_score', label: '% Days Below Acuity', weight: 15, description: 'Percentage of days failing to staff to acuity.' },
  { key: 'factor3_score', label: 'Understaffing Cost', weight: 20, description: 'Projected understaffing cost without credit.' },
  { key: 'factor4_score', label: 'Staffing Citations', weight: 5, description: 'Staffing-related survey citations.' },
  { key: 'factor5_score', label: 'Abuse Citations', weight: 5, description: 'Abuse and neglect citations.' },
  { key: 'factor6_score', label: 'RP Paid/Day', weight: 10, description: 'RP payments per resident per day.' },
  { key: 'factor7_score', label: 'RP % Revenue', weight: 10, description: 'RP as percentage of revenue.' },
  { key: 'factor8_score', label: 'RP Flags', weight: 5, description: 'Declared + hidden related party flags.' },
  { key: 'factor9_score', label: 'Other Flags', weight: 5, description: 'Other flagged transactions.' },
  { key: 'factor10_score', label: 'Star Rating', weight: 5, description: 'CMS star rating (lower = higher risk).' },
];

/* ───────────────── FacilityCard (state-scoped view) ───────────────── */

interface FacilityCardData {
  name: string;
  ccn: string;
  address: string | null;
  avgCensus: number | null;
  riskScore: number | null;
  riskFactors: any[];
  noCostReportData: boolean;
  years: FacilityYear[];
  totalMdsCost: number;
  totalMdsCostNoCredit: number;
  totalHybridCost: number;
  totalHybridCostNoCredit: number;
  acuityGap: number | null;
  pctDaysFailing: number | null;
  hybridPctDaysFailing: number | null;
  ownershipDate: string | null;
  ownershipEndDate: string | null;
  isCurrentOwner: boolean | null;
  chowExcludedYears: any[];
  groupHistory: GroupHistoryPeriod[];
}

function FacilityCard({ fac, schemeId, navigate, stateAcuity, stateAcuityByYear, model }: { fac: FacilityCardData; schemeId: string; navigate: any; stateAcuity: Record<string, number | null>; stateAcuityByYear: Record<number, any>; model: 'anticipated' | 'hybrid' }) {
  const [expanded, setExpanded] = useState(false);
  const [cardTab, setCardTab] = useState(0);
  const [expandedCardYear, setExpandedCardYear] = useState<number | null>(null);
  const [ownershipData, setOwnershipData] = useState<OwnershipData | null>(null);
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [ownershipFetched, setOwnershipFetched] = useState(false);
  const riskQuintile = fac.riskScore != null ? Math.ceil(fac.riskScore / 20) : null;
  // Header metrics follow the page-level model toggle
  const hdrModelLabel = model === 'hybrid' ? 'Hybrid' : 'Anticipated';
  const hdrPctDays = model === 'hybrid' ? fac.hybridPctDaysFailing : fac.pctDaysFailing;
  const hdrCostNoCredit = model === 'hybrid' ? fac.totalHybridCostNoCredit : fac.totalMdsCostNoCredit;
  const hdrCostWithCredit = model === 'hybrid' ? fac.totalHybridCost : fac.totalMdsCost;

  // Lazy-load ownership data when Ownership tab is first clicked
  useEffect(() => {
    if (cardTab !== 1 || ownershipFetched || !schemeId) return;
    setOwnershipLoading(true);
    const fetchOwnership = async () => {
      try {
        const res = await api.get(`/schemes/${schemeId}/understaffing/facilities/${fac.ccn}/ownership/`);
        setOwnershipData(res.data);
      } catch (err) {
        console.error('Failed to fetch ownership', err);
      } finally {
        setOwnershipLoading(false);
        setOwnershipFetched(true);
      }
    };
    fetchOwnership();
  }, [cardTab, ownershipFetched, schemeId, fac.ccn]);

  const formatMonth = (dateStr: string) => {
    const [year, month] = dateStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: 2.5, py: 2,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' },
          userSelect: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <ExpandMore sx={{
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s', color: 'text.secondary', fontSize: 20, flexShrink: 0,
          }} />
          <Box sx={{ width: 340, flexShrink: 0 }}>
            <Typography
              variant="subtitle1" fontWeight={700}
              sx={{ lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {fac.name}
            </Typography>
            {fac.address && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {fac.address}
              </Typography>
            )}
          </Box>
          <Box sx={{ flexShrink: 0, pl: 2.5, borderLeft: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.5 }}>
            {/* Total Acuity Gap (Anticipated) removed from facility card header
            <Box sx={{ width: 130, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.3 }}>Total Acuity Gap<br />(Anticipated)</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2, color: fac.acuityGap != null && fac.acuityGap < 0 ? 'error.main' : 'success.main' }}>{fac.acuityGap != null ? `${fac.acuityGap > 0 ? '+' : ''}${fac.acuityGap.toFixed(1)}%` : '-'}</Typography>
            </Box>
            */}
            <Box sx={{ width: 130, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.3 }}>% Days Failing Acuity<br />({hdrModelLabel})</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2, color: hdrPctDays != null && hdrPctDays > 50 ? 'error.main' : 'text.primary' }}>{hdrPctDays != null ? `${hdrPctDays.toFixed(1)}%` : '-'}</Typography>
            </Box>
            <Box sx={{ width: 190, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.3 }}>Projected Total Understaffing<br />({hdrModelLabel}, No Credit)</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{formatMoneyFull(hdrCostNoCredit)}</Typography>
            </Box>
            <Box sx={{ width: 190, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.3 }}>Projected Total Understaffing<br />({hdrModelLabel}, w/ Credit)</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{formatMoneyFull(Math.max(0, hdrCostWithCredit))}</Typography>
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center" sx={{ flexShrink: 0, ml: 2 }}>
          <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-end">
          {fac.riskScore != null && (
            <Tooltip arrow placement="left" title={
              <Box sx={{ p: 0.5, minWidth: 220 }}>
                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Risk Score: {Math.round(fac.riskScore!)} / 100
                </Typography>
                {fac.riskFactors.length > 0 && (
                  <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.3)', pt: 0.5 }}>
                    {fac.riskFactors.map((f: any, idx: number) => (
                      <Typography key={f.factor ?? idx} variant="caption" sx={{ display: 'block', lineHeight: 1.6 }}>
                        {f.label}: <b>{Math.round(f.score)}/100</b> ({f.weight}%){f.raw ? ` — ${f.raw}` : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            }>
              <Chip label={`Risk: ${Math.round(fac.riskScore)}`} size="small" sx={{ fontWeight: 700, fontSize: '0.75rem', backgroundColor: getRiskColor(fac.riskScore), color: 'white' }} />
            </Tooltip>
          )}
          {fac.avgCensus != null && (
            <Chip label={`Avg Census: ${Math.round(fac.avgCensus)}`} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
          )}
          {fac.ownershipDate && (() => {
            const start = new Date(fac.ownershipDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            if (fac.isCurrentOwner === false && fac.ownershipEndDate) {
              const end = new Date(fac.ownershipEndDate + '-01T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              return <Chip label={`${start} – ${end}`} size="small" color="warning" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />;
            }
            return <Chip label={`Since ${start}`} size="small" color="info" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />;
          })()}
          </Box>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Open facility detail" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); window.open(`/schemes/snf-understaffing/facility/${fac.ccn}`, '_blank'); }}
              sx={{ color: 'primary.main' }}
            >
              <OpenInNew sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Expanded content with tabs */}
      {expanded && (
        <>
          <Tabs
            value={cardTab}
            onChange={(_e, v) => setCardTab(v)}
            sx={{
              minHeight: 36, px: 2.5, borderBottom: 1, borderColor: 'divider',
              '& .MuiTabs-indicator': { height: 2 },
              '& .MuiTab-root': { minHeight: 36, fontSize: '0.8rem', fontWeight: 500, textTransform: 'none', px: 2 },
            }}
          >
            <Tab label="Understaffing" />
            <Tab label="Ownership" />
          </Tabs>

          {/* Understaffing tab */}
          {cardTab === 0 && (() => {
            const isHybrid = model === 'hybrid';
            const rows = fac.years.sort((a, b) => a.year - b.year);
            const getAcuity = (fy: any) => {
              const val = isHybrid ? fy.hybrid_pct_acuity_staffed_total : fy.ant_pct_acuity_staffed_total;
              return val != null ? val - 100 : null;
            };
            const getDaysFailing = (fy: any) => isHybrid ? fy.hybrid_pct_days_any_below : fy.pct_ant_days_any_below;
            const getCostNoCredit = (fy: any) => isHybrid ? fy.hybrid_mds_understaffing_cost_no_credit : fy.mds_understaffing_cost_no_credit;
            const getCostWithCredit = (fy: any) => isHybrid ? fy.hybrid_mds_understaffing_cost : fy.mds_understaffing_cost;
            const nonTransitionRows = rows.filter((fy: any) => !fy.is_transition_year);
            const totalNoCredit = nonTransitionRows.reduce((s: number, fy: any) => s + (getCostNoCredit(fy) || 0), 0);
            const totalWithCredit = nonTransitionRows.reduce((s: number, fy: any) => s + Math.max(getCostWithCredit(fy) || 0, 0), 0);
            // Overall acuity gap
            const totalAcuity = fac.acuityGap;
            const totalDays = fac.pctDaysFailing;
            const fmtAcuity = (v: number | null) => {
              if (v == null) return '-';
              return <span style={{ color: v < 0 ? '#d32f2f' : '#2e7d32', fontWeight: 600 }}>{v > 0 ? '+' : ''}{v.toFixed(1)}%</span>;
            };
            const fmtDays = (v: number | null) => {
              if (v == null) return '-';
              return <span style={{ color: v > 50 ? '#d32f2f' : undefined, fontWeight: 600 }}>{v.toFixed(1)}%</span>;
            };

            const MAX_PCT = 140;
            const barRow = (label: string, pct: number | null, stPct: number | null) => {
              const v = pct ?? 0;
              const sv = stPct ?? 0;
              const w = Math.min(v, MAX_PCT) / MAX_PCT * 100;
              const sw = Math.min(sv, MAX_PCT) / MAX_PCT * 100;
              const marker = (100 / MAX_PCT) * 100;
              return (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, width: 40, color: 'text.secondary', flexShrink: 0 }}>{label}</Typography>
                  <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
                    {stPct != null && <Box sx={{ position: 'absolute', left: 0, top: 3, bottom: 3, width: `${sw}%`, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px dashed #90caf9' }} />}
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${w}%`, backgroundColor: v < 100 ? '#ef5350' : '#66bb6a', opacity: 0.8, borderRadius: 1 }} />
                    <Box sx={{ position: 'absolute', left: `${marker}%`, top: -2, bottom: -2, width: 2, backgroundColor: '#212121', opacity: 0.7, zIndex: 2 }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, width: 50, textAlign: 'right', color: pct != null && pct < 100 ? '#d32f2f' : '#2e7d32' }}>
                    {pct != null ? `${(pct - 100).toFixed(1)}%` : '-'}
                  </Typography>
                </Box>
              );
            };

            return (
              <Box sx={{ px: 2.5, py: 1.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={700}>Year-by-Year Breakdown</Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', py: 0.75 } }}>
                      <TableCell sx={{ width: 80 }}>Year</TableCell>
                      <TableCell align="right">Total Acuity Gap</TableCell>
                      <TableCell align="right">% Days Failing to Staff to Acuity</TableCell>
                      <TableCell align="right">Projected Total Understaffing Without Credit</TableCell>
                      <TableCell align="right">Projected Total Understaffing With Credit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((fy: any) => {
                      const yearExpanded = expandedCardYear === fy.year;
                      const pbjRn = isHybrid ? fy.hy_pbj_rn : fy.ant_pbj_rn_hrs;
                      const pbjLpn = isHybrid ? fy.hy_pbj_lpn : fy.ant_pbj_lpn_hrs;
                      const pbjCna = isHybrid ? fy.hy_pbj_cna : fy.ant_pbj_cna_hrs;
                      const expRn = isHybrid ? fy.hy_exp_rn : fy.ant_expected_rn_hrs_hours;
                      const expLpn = isHybrid ? fy.hy_exp_lpn : fy.ant_expected_lpn_hrs_hours;
                      const expAide = isHybrid ? fy.hy_exp_aide : fy.ant_expected_cna_hrs_hours;
                      const acuityRn = expRn > 0 ? Math.round((pbjRn / expRn) * 1000) / 10 : null;
                      const acuityLpn = expLpn > 0 ? Math.round((pbjLpn / expLpn) * 1000) / 10 : null;
                      const acuityAide = expAide > 0 ? Math.round((pbjCna / expAide) * 1000) / 10 : null;
                      const acuityTotal = (expRn + expLpn + expAide) > 0 ? Math.round(((pbjRn + pbjLpn + pbjCna) / (expRn + expLpn + expAide)) * 1000) / 10 : null;
                      const isTransition = fy.is_transition_year;
                      return (
                        <React.Fragment key={fy.year}>
                          <TableRow
                            onClick={() => setExpandedCardYear(yearExpanded ? null : fy.year)}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' }, '& td': { py: 0.75, fontSize: '0.85rem' }, opacity: isTransition ? 0.45 : 1 }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <ExpandMore sx={{ fontSize: 16, color: 'text.disabled', transform: yearExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                                <Typography fontWeight={600}>{fy.year}</Typography>
                                {isTransition && <Chip label="Transition" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, ml: 0.5 }} />}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{fmtAcuity(getAcuity(fy))}</TableCell>
                            <TableCell align="right">{fmtDays(getDaysFailing(fy))}</TableCell>
                            <TableCell align="right">{formatMoneyFull(getCostNoCredit(fy))}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneyFull(Math.max(0, getCostWithCredit(fy) || 0))}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={5} sx={{ p: 0, borderBottom: yearExpanded ? '2px solid' : 'none', borderColor: 'divider' }}>
                              <Collapse in={yearExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 3, py: 1.5, backgroundColor: '#fafbfc' }}>
                                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                                      {fy.year} Staffing as % of Acuity by Staff Type
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ width: 14, height: 10, backgroundColor: '#66bb6a', opacity: 0.8, borderRadius: 0.5 }} />
                                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Facility</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ width: 14, height: 10, backgroundColor: '#e3f2fd', border: '1px dashed #90caf9', borderRadius: 0.5 }} />
                                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>State Avg</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ width: 12, height: 12, borderLeft: '2.5px solid #212121', opacity: 0.7 }} />
                                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>100%</Typography>
                                    </Box>
                                  </Box>
                                  {(() => {
                                    const isH = model === 'hybrid';
                                    const yd = stateAcuityByYear[fy.year];
                                    const sa = yd ? {
                                      rn: isH ? yd.hybrid_acuity_rn : yd.avg_acuity_rn,
                                      lpn: isH ? yd.hybrid_acuity_lpn : yd.avg_acuity_lpn,
                                      aide: isH ? yd.hybrid_acuity_aide : yd.avg_acuity_aide,
                                    } : stateAcuity;
                                    return (
                                      <>
                                        {barRow('RN', acuityRn, sa.rn ?? null)}
                                        {barRow('LPN', acuityLpn, sa.lpn ?? null)}
                                        {barRow('CNA', acuityAide, sa.aide ?? null)}
                                      </>
                                    );
                                  })()}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                    {rows.length > 1 && (() => {
                      const totalExpanded = expandedCardYear === -1;
                      // Aggregate all years for the total bar chart
                      const sumVal = (key: string) => rows.reduce((s: number, fy: any) => s + (fy[key] || 0), 0);
                      const tPbjRn = isHybrid ? sumVal('hy_pbj_rn') : sumVal('ant_pbj_rn_hrs');
                      const tPbjLpn = isHybrid ? sumVal('hy_pbj_lpn') : sumVal('ant_pbj_lpn_hrs');
                      const tPbjCna = isHybrid ? sumVal('hy_pbj_cna') : sumVal('ant_pbj_cna_hrs');
                      const tExpRn = isHybrid ? sumVal('hy_exp_rn') : sumVal('ant_expected_rn_hrs_hours');
                      const tExpLpn = isHybrid ? sumVal('hy_exp_lpn') : sumVal('ant_expected_lpn_hrs_hours');
                      const tExpAide = isHybrid ? sumVal('hy_exp_aide') : sumVal('ant_expected_cna_hrs_hours');
                      const tAcuityRn = tExpRn > 0 ? Math.round((tPbjRn / tExpRn) * 1000) / 10 : null;
                      const tAcuityLpn = tExpLpn > 0 ? Math.round((tPbjLpn / tExpLpn) * 1000) / 10 : null;
                      const tAcuityAide = tExpAide > 0 ? Math.round((tPbjCna / tExpAide) * 1000) / 10 : null;
                      return (
                        <>
                          <TableRow
                            onClick={() => setExpandedCardYear(totalExpanded ? null : -1)}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' }, '& td': { py: 0.75, fontSize: '0.85rem', fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <ExpandMore sx={{ fontSize: 16, color: 'text.disabled', transform: totalExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                                <Typography fontWeight={700}>Total</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{fmtAcuity(totalAcuity)}</TableCell>
                            <TableCell align="right">{fmtDays(totalDays)}</TableCell>
                            <TableCell align="right">{formatMoneyFull(totalNoCredit)}</TableCell>
                            <TableCell align="right">{formatMoneyFull(Math.max(0, totalWithCredit))}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={5} sx={{ p: 0, borderBottom: totalExpanded ? '2px solid' : 'none', borderColor: 'divider' }}>
                              <Collapse in={totalExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 3, py: 1.5, backgroundColor: '#fafbfc' }}>
                                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                                      All Years — Staffing as % of Acuity by Staff Type
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ width: 14, height: 10, backgroundColor: '#66bb6a', opacity: 0.8, borderRadius: 0.5 }} />
                                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Facility</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ width: 14, height: 10, backgroundColor: '#e3f2fd', border: '1px dashed #90caf9', borderRadius: 0.5 }} />
                                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>State Avg</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ width: 12, height: 12, borderLeft: '2.5px solid #212121', opacity: 0.7 }} />
                                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>100%</Typography>
                                    </Box>
                                  </Box>
                                  {barRow('RN', tAcuityRn, stateAcuity.rn)}
                                  {barRow('LPN', tAcuityLpn, stateAcuity.lpn)}
                                  {barRow('CNA', tAcuityAide, stateAcuity.aide)}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </Box>
            );
          })()}

          {/* Ownership tab */}
          {cardTab === 1 && (
            <Box sx={{ p: 2.5 }}>
              {ownershipLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : ownershipData ? (
                <>
                  {ownershipData.ownership_periods && ownershipData.ownership_periods.length > 0 && (() => {
                    const periods = ownershipData.ownership_periods!;
                    const chowEvents = [...(ownershipData.chow_events || [])].sort(
                      (a, b) => b.effective_date.localeCompare(a.effective_date)
                    );

                    type TimelineNode = { type: 'period'; data: OwnershipPeriod } | { type: 'chow'; data: ChowEvent };
                    const timeline: TimelineNode[] = [];
                    const usedChows = new Set<string>();

                    periods.forEach((period) => {
                      timeline.push({ type: 'period', data: period });
                      const matchingChow = chowEvents.find((e) => {
                        const key = e.effective_date;
                        if (usedChows.has(key)) return false;
                        const chowMonth = key.substring(0, 7);
                        return chowMonth >= period.start_month && chowMonth <= period.end_month;
                      });
                      if (matchingChow) {
                        usedChows.add(matchingChow.effective_date);
                        timeline.push({ type: 'chow', data: matchingChow });
                      }
                    });

                    return (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                          Ownership History
                        </Typography>
                        <Box
                          sx={{
                            position: 'relative', pl: 4,
                            '&::before': {
                              content: '""', position: 'absolute', left: 11, top: 8, bottom: 8,
                              width: 2, backgroundColor: 'grey.300',
                            },
                          }}
                        >
                          {timeline.map((node, idx) => {
                            if (node.type === 'period') {
                              const period = node.data as OwnershipPeriod;
                              const dotColor = period.is_current ? '#4caf50' : '#9e9e9e';
                              return (
                                <Box key={period.enrollment_id} sx={{ position: 'relative', pb: idx < timeline.length - 1 ? 2 : 0 }}>
                                  <Box sx={{
                                    position: 'absolute', left: -27, top: 14, width: 12, height: 12,
                                    borderRadius: '50%', backgroundColor: dotColor,
                                    border: '2px solid white', boxShadow: `0 0 0 2px ${dotColor}`, zIndex: 1,
                                  }} />
                                  <Box sx={{
                                    p: 2, backgroundColor: 'background.paper', borderRadius: 2,
                                    border: '1px solid', borderColor: period.is_current ? 'success.light' : 'divider',
                                  }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                      <Box>
                                        <Typography variant="body2" fontWeight={700}>{period.facility_name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {formatMonth(period.start_month)} {period.is_current ? '– Present' : `– ${formatMonth(period.end_month)}`}
                                        </Typography>
                                      </Box>
                                      {period.is_current && (
                                        <Chip label="Current" size="small" color="success" variant="outlined" />
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                      {period.direct_owners.length > 0 && (
                                        <Box sx={{ minWidth: 180 }}>
                                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                                            5%+ Direct Ownership
                                          </Typography>
                                          {period.direct_owners.map((owner, oidx) => (
                                            <Box key={oidx} display="flex" alignItems="center" gap={1} mt={0.5}>
                                              <Typography variant="body2" fontWeight={500}>{owner.owner_name}</Typography>
                                              {owner.percentage != null && (
                                                <Chip label={`${owner.percentage.toFixed(0)}%`} size="small" sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />
                                              )}
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                      {period.indirect_owners.length > 0 && (
                                        <Box sx={{ minWidth: 180 }}>
                                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                                            5%+ Indirect Ownership
                                          </Typography>
                                          {period.indirect_owners.map((owner, oidx) => (
                                            <Box key={oidx} display="flex" alignItems="center" gap={1} mt={0.5}>
                                              <Typography variant="body2" fontWeight={500}>{owner.owner_name}</Typography>
                                              {owner.percentage != null && (
                                                <Chip label={`${owner.percentage.toFixed(0)}%`} size="small" sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />
                                              )}
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                      {period.managerial_control.length > 0 && (
                                        <Box sx={{ minWidth: 180 }}>
                                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                                            Managerial Control
                                          </Typography>
                                          {period.managerial_control.map((owner, oidx) => (
                                            <Box key={oidx} display="flex" alignItems="center" gap={1} mt={0.5}>
                                              <Chip label={owner.type === 'O' ? 'Org' : 'Ind'} size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }} />
                                              <Typography variant="body2" fontWeight={500}>{owner.owner_name}</Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            } else {
                              const chow = node.data as ChowEvent;
                              return (
                                <Box key={`chow-${chow.effective_date}`} sx={{ position: 'relative', pb: 2 }}>
                                  <Box sx={{
                                    position: 'absolute', left: -29, top: 8, width: 16, height: 16,
                                    zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    <Box sx={{
                                      width: 10, height: 10, backgroundColor: '#ff9800',
                                      transform: 'rotate(45deg)', border: '2px solid white', boxShadow: '0 0 0 2px #ff9800',
                                    }} />
                                  </Box>
                                  <Box sx={{
                                    py: 1, px: 2, backgroundColor: '#fff8e1', borderRadius: 1.5,
                                    border: '1px solid', borderColor: '#ffe0b2',
                                    display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                                  }}>
                                    <Chip
                                      icon={<SwapHoriz sx={{ fontSize: 14 }} />}
                                      label="CHOW" size="small"
                                      sx={{
                                        height: 22, backgroundColor: '#ff9800', color: 'white',
                                        fontWeight: 700, fontSize: '0.65rem',
                                        '& .MuiChip-icon': { color: 'white' }, '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#e65100' }}>
                                      {(() => {
                                        const [year, month, day] = chow.effective_date.split('-');
                                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                        return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
                                      })()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {chow.seller_name} &#8594; {chow.buyer_name}
                                    </Typography>
                                    {chow.chow_type_text && (
                                      <Chip label={chow.chow_type_text} size="small" variant="outlined"
                                        sx={{ height: 18, borderColor: '#ffcc80', color: 'text.secondary', '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              );
                            }
                          })}
                        </Box>
                      </Box>
                    );
                  })()}
                  {!(ownershipData.ownership_periods && ownershipData.ownership_periods.length > 0) && (
                    <Typography variant="body2" color="text.secondary">
                      No ownership data available for this facility.
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No ownership data available.
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

/* ───────────────── StateRow (national view, expandable) ───────────────── */

function StateRow({ stateData, navigate }: { stateData: StateRollup; navigate: any }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', '& td': { py: 1.25 } }} onClick={() => setOpen(!open)}>
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small">{open ? <KeyboardArrowDown /> : <KeyboardArrowRight />}</IconButton>
        </TableCell>
        <TableCell>
          <Typography fontWeight={700} fontSize="0.9rem" color="primary"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={(e) => { e.stopPropagation(); navigate(`/schemes/snf-understaffing/state/${stateData.state}`); }}
          >{stateData.state}</Typography>
        </TableCell>
        <TableCell align="right">{stateData.num_facilities}</TableCell>
        <TableCell align="right"><Typography fontWeight={600} fontSize="0.85rem">{formatMoney(stateData.total_ant_cost)}</Typography></TableCell>
        <TableCell align="right">{formatMoney(stateData.total_ant_cost_no_credit)}</TableCell>
        <TableCell align="right">{formatMoney(stateData.hybrid_mds_cost)}</TableCell>
        <TableCell align="right">{formatMoney(stateData.hybrid_mds_cost_no_credit)}</TableCell>
        <TableCell align="right">{formatMoney(stateData.total_rp_paid)}</TableCell>
        <TableCell align="right">{formatNumber(stateData.total_harm_citations)}</TableCell>
        <TableCell align="right">{formatNumber(stateData.avg_overall_rating, 1)}</TableCell>
        <TableCell align="right">{formatMoney(stateData.total_fine_amount)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, pl: 5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, py: 0.5 } }}>
                    <TableCell>Facility</TableCell>
                    <TableCell align="right">Avg Census</TableCell>
                    <TableCell align="right">MDS Cost</TableCell>
                    <TableCell align="right">RP Paid</TableCell>
                    <TableCell align="right">Harm Cites</TableCell>
                    <TableCell align="right">Rating</TableCell>
                    <TableCell align="right">Risk</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stateData.facilities || []).map((fac: any) => {
                    const riskQuintile = fac.risk_score != null ? Math.ceil(fac.risk_score / 20) : null;
                    return (
                      <TableRow key={fac.ccn} hover sx={{ cursor: 'pointer', '& td': { py: 0.75 } }}
                        onClick={() => navigate(`/schemes/snf-understaffing/facility/${fac.ccn}`)}>
                        <TableCell>
                          <Typography fontSize="0.82rem" fontWeight={500} color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                            {fac.facility_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fac.avg_census != null ? Math.round(fac.avg_census) : '-'}</TableCell>
                        <TableCell align="right"><Typography fontWeight={600} fontSize="0.82rem">{formatMoney(fac.mds_cost)}</Typography></TableCell>
                        <TableCell align="right">{formatMoney(fac.rp_paid)}</TableCell>
                        <TableCell align="right">{formatNumber(fac.harm_citations)}</TableCell>
                        <TableCell align="right">{formatNumber(fac.avg_overall_rating, 1)}</TableCell>
                        <TableCell align="right">
                          {riskQuintile != null ? (
                            <Chip label={riskQuintile} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, minWidth: 30, backgroundColor: getRiskColor(fac.risk_score), color: 'white' }} />
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/* ───────────────── component ───────────────── */

export default function UnderstaffingOwnerGroupPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromState = searchParams.get('state') || '';
  const fromPage = searchParams.get('from') || '';

  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [data, setData] = useState<OwnerGroupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  // Single Anticipated/Hybrid toggle for the whole Facilities tab (summary cards + facility cards). Defaults to Hybrid.
  const [facilitiesModel, setFacilitiesModel] = useState<'anticipated' | 'hybrid'>('hybrid');
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [expandedOwner, setExpandedOwner] = useState<string | null>(null);
  const [rpData, setRpData] = useState<any>(null);
  const [rpLoading, setRpLoading] = useState(false);
  const [rpSubTab, setRpSubTab] = useState(0);
  const [ceData, setCeData] = useState<any>(null);
  const [ceLoading, setCeLoading] = useState(false);
  const [stateAcuity, setStateAcuity] = useState<Record<string, number | null>>({});
  const [stateAcuityByYear, setStateAcuityByYear] = useState<Record<number, any>>({});

  const backUrl = fromPage === 'national'
    ? '/schemes/snf-understaffing?view=ownerGroups'
    : fromState
      ? `/schemes/snf-understaffing/state/${fromState}?view=ownerGroups`
      : '/schemes/snf-understaffing?view=ownerGroups';
  const backLabel = fromPage === 'national'
    ? 'Back to National Overview'
    : fromState
      ? `Back to ${fromState}`
      : 'Back to National Overview';

  // Fetch scheme ID
  useEffect(() => {
    const fetchScheme = async () => {
      try {
        const res = await api.get('/schemes/');
        const schemes = res.data.results ?? res.data;
        const s = schemes.find((s: any) => s.code === 'snf-understaffing');
        if (s) setSchemeId(s.id);
        else { setError('Scheme not found'); setLoading(false); }
      } catch {
        setError('Failed to load scheme'); setLoading(false);
      }
    };
    fetchScheme();
  }, []);

  // Fetch owner group detail (pass state if present)
  useEffect(() => {
    if (!schemeId || !groupId) return;
    setLoading(true);
    setError(null);

    const fetchDetail = async () => {
      try {
        const params: any = {};
        if (fromState) params.state = fromState;
        const res = await api.get(
          `/schemes/${schemeId}/understaffing/owner-group/${encodeURIComponent(groupId)}/`,
          { params },
        );
        setData(res.data);
      } catch (err: any) {
        if (err.response?.status === 404) setError('Owner group not found');
        else setError('Failed to load owner group data');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [schemeId, groupId, fromState]);

  // Fetch state acuity averages for bar chart comparison
  useEffect(() => {
    if (!schemeId || !fromState) return;
    const fetchStateCtx = async () => {
      try {
        const res = await api.get(`/schemes/${schemeId}/understaffing/state-context/`, { params: { state: fromState } });
        setStateAcuity({
          rn: res.data.avg_acuity_rn ?? null,
          lpn: res.data.avg_acuity_lpn ?? null,
          aide: res.data.avg_acuity_aide ?? null,
          total: res.data.avg_acuity_gap ?? null,
        });
        if (res.data.acuity_by_year) {
          setStateAcuityByYear(res.data.acuity_by_year);
        }
      } catch { /* silent */ }
    };
    fetchStateCtx();
  }, [schemeId, fromState]);

  // Fetch RP summary when Related Parties tab is selected
  useEffect(() => {
    if (tabValue !== 1 || !schemeId || !groupId) return;
    if (rpData) return;
    setRpLoading(true);
    const fetchRp = async () => {
      try {
        const params: any = {};
        if (fromState) params.state = fromState;
        const res = await api.get(
          `/schemes/${schemeId}/understaffing/owner-group/${encodeURIComponent(groupId)}/related-parties/`,
          { params },
        );
        setRpData(res.data);
      } catch {
        // silently fail — tab will show empty state
      } finally {
        setRpLoading(false);
      }
    };
    fetchRp();
  }, [tabValue, schemeId, groupId, fromState]);

  // Fetch check engine signals when Additional Analysis or Other Flagged sub-tab is selected
  useEffect(() => {
    if (tabValue !== 1 || (rpSubTab !== 2 && rpSubTab !== 3) || !schemeId || !groupId) return;
    if (ceData) return;
    setCeLoading(true);
    const fetchCe = async () => {
      try {
        const params: any = {};
        if (fromState) params.state = fromState;
        const res = await api.get(
          `/schemes/${schemeId}/understaffing/owner-group/${encodeURIComponent(groupId)}/check-engine/`,
          { params },
        );
        setCeData(res.data);
      } catch {
        // silently fail
      } finally {
        setCeLoading(false);
      }
    };
    fetchCe();
  }, [tabValue, rpSubTab, schemeId, groupId, fromState]);

  // If no state param and the group only operates in one state, default to that state
  useEffect(() => {
    if (!fromState && data?.states?.length === 1 && data.states[0].state) {
      const params = new URLSearchParams(searchParams);
      params.set('state', data.states[0].state);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [fromState, data, searchParams, navigate]);

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 4 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(backUrl)} sx={{ mb: 2 }}>{backLabel}</Button>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" variant="h6">{error || 'Not found'}</Typography>
          </Paper>
        </Box>
      </Layout>
    );
  }

  const { group, states, members } = data;
  const isStateScoped = !!fromState && !!data.facility_years;

  // Compute totals from states rollup
  const totals = states.reduce(
    (acc, s: any) => ({
      mds_cost: acc.mds_cost + (s.total_ant_cost || 0),
      mds_cost_no_credit: acc.mds_cost_no_credit + (s.total_ant_cost_no_credit || 0),
      hybrid_mds_cost: acc.hybrid_mds_cost + (s.hybrid_mds_cost || 0),
      hybrid_mds_cost_no_credit: acc.hybrid_mds_cost_no_credit + (s.hybrid_mds_cost_no_credit || 0),
      rp_paid: acc.rp_paid + (s.total_rp_paid || 0),
      harm_citations: acc.harm_citations + (s.total_harm_citations || 0),
      fine_amount: acc.fine_amount + (s.total_fine_amount || 0),
      avg_acuity_gap: s.avg_acuity_gap ?? acc.avg_acuity_gap,
      pct_ant_days_any_below: s.pct_ant_days_any_below ?? acc.pct_ant_days_any_below,
      hybrid_pct_days_any_below: s.hybrid_pct_days_any_below ?? acc.hybrid_pct_days_any_below,
      pct_days_meeting_state_min: s.pct_days_meeting_state_min ?? acc.pct_days_meeting_state_min,
    }),
    { mds_cost: 0, mds_cost_no_credit: 0, hybrid_mds_cost: 0, hybrid_mds_cost_no_credit: 0, rp_paid: 0, harm_citations: 0, fine_amount: 0, avg_acuity_gap: null as number | null, pct_ant_days_any_below: null as number | null, hybrid_pct_days_any_below: null as number | null, pct_days_meeting_state_min: null as number | null },
  );

  // Derived values for the single Facilities-tab model toggle (summary cards).
  const isHybridModel = facilitiesModel === 'hybrid';
  const modelLabel = isHybridModel ? 'Hybrid' : 'Anticipated';
  const summaryDaysFailing = isHybridModel ? totals.hybrid_pct_days_any_below : totals.pct_ant_days_any_below;
  const summaryCostNoCredit = isHybridModel ? totals.hybrid_mds_cost_no_credit : totals.mds_cost_no_credit;
  const summaryCostWithCredit = isHybridModel ? totals.hybrid_mds_cost : totals.mds_cost;

  // Build facility cards for state-scoped view
  let facilityCards: FacilityCardData[] = [];
  if (isStateScoped) {
    const facilityYears = data.facility_years!;
    const addresses = data.facility_addresses || {};
    const facTotals = data.facility_totals || {};
    const ownerDates = data.ownership_dates || {};
    const currentOwn = data.current_ownership || {};
    const ownerEndDates = data.ownership_end_dates || {};
    const chowExcl = data.chow_exclusions || {};
    const groupHist = data.facility_group_history || {};
    const byCcn: Record<string, FacilityCardData> = {};
    for (const fy of facilityYears) {
      if (!byCcn[fy.ccn]) {
        const raw = fy.top_risk_factors;
        const riskFactors = Array.isArray(raw) ? raw.filter((f: any) => f && typeof f === 'object')
          : typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return []; } })()
          : [];
        byCcn[fy.ccn] = {
          name: fy.facility_name || fy.ccn,
          ccn: fy.ccn,
          address: addresses[fy.ccn] || null,
          avgCensus: null,
          riskScore: fy.risk_score,
          riskFactors,
          noCostReportData: false,
          years: [],
          totalMdsCost: facTotals[fy.ccn]?.mds_understaffing_cost || 0,
          totalMdsCostNoCredit: facTotals[fy.ccn]?.mds_understaffing_cost_no_credit || 0,
          totalHybridCost: facTotals[fy.ccn]?.hybrid_mds_cost || 0,
          totalHybridCostNoCredit: facTotals[fy.ccn]?.hybrid_mds_cost_no_credit || 0,
          acuityGap: null,
          pctDaysFailing: null,
          hybridPctDaysFailing: null,
          ownershipDate: ownerDates[fy.ccn] || null,
          ownershipEndDate: ownerEndDates[fy.ccn] || null,
          isCurrentOwner: currentOwn[fy.ccn] ?? null,
          chowExcludedYears: chowExcl[fy.ccn] || [],
          groupHistory: groupHist[fy.ccn] || [],
        };
      }
      byCcn[fy.ccn].years.push(fy);
      if (fy.avg_census != null) byCcn[fy.ccn].avgCensus = fy.avg_census;
    }
    for (const fac of Object.values(byCcn)) {
      fac.noCostReportData = fac.years.length > 0 && fac.years.every(fy => fy.no_cost_report_data);
      const countedYears = fac.years.filter((fy: any) => !fy.is_transition_year);
      // Compute overall acuity gap: weighted by actual hours (sum PBJ / sum expected)
      let totalPbj = 0, totalExp = 0;
      for (const fy of countedYears) {
        const pbj = (fy.ant_pbj_rn_hrs || 0) + (fy.ant_pbj_lpn_hrs || 0) + (fy.ant_pbj_cna_hrs || 0);
        const exp = (fy.ant_expected_rn_hrs_hours || 0) + (fy.ant_expected_lpn_hrs_hours || 0) + (fy.ant_expected_cna_hrs_hours || 0);
        totalPbj += pbj;
        totalExp += exp;
      }
      fac.acuityGap = totalExp > 0 ? (totalPbj / totalExp * 100) - 100 : null;
      // Compute % days failing: sum invalid days / sum total days (anticipated + hybrid)
      let totalInvalid = 0, totalDays = 0;
      let totalInvalidHybrid = 0, totalDaysHybrid = 0;
      for (const fy of countedYears) {
        if (fy.ant_days_any_below != null && fy.days_with_mds_and_pbj_data != null) {
          totalInvalid += fy.ant_days_any_below;
          totalDays += fy.days_with_mds_and_pbj_data;
        }
        if (fy.hybrid_days_any_below != null && fy.days_with_mds_and_pbj_data != null) {
          totalInvalidHybrid += fy.hybrid_days_any_below;
          totalDaysHybrid += fy.days_with_mds_and_pbj_data;
        }
      }
      fac.pctDaysFailing = totalDays > 0 ? (totalInvalid / totalDays * 100) : null;
      fac.hybridPctDaysFailing = totalDaysHybrid > 0 ? (totalInvalidHybrid / totalDaysHybrid * 100) : null;
    }
    facilityCards = Object.values(byCcn).sort((a, b) => b.totalMdsCost - a.totalMdsCost);
  }

  // Build sub-groups from org members that own >1 facility
  // Prioritize smaller/more-specific holdings (tier 1 first, then smallest first)
  // so regional LLCs aren't swallowed by larger management companies
  interface SubGroup {
    name: string;
    ccns: Set<string>;
    cards: FacilityCardData[];
    tier1Owners: { name: string; type: string; count: number }[];
  }
  const facilitySubGroups: SubGroup[] = [];
  if (isStateScoped && members.length > 0 && facilityCards.length > 1) {
    const holdingCos = members
      .filter((m: any) => m.type_owner === 'O' && m.num_facilities > 1)
      .sort((a: any, b: any) => a.tier - b.tier || a.num_facilities - b.num_facilities);

    const assigned = new Set<string>();
    // All tier-1 members for sub-group owner lookup
    const tier1Members = members.filter((m: any) => m.tier === 1 && m.owner_name);

    for (const hc of holdingCos) {
      const ccnSet = new Set<string>(hc.ccn_list || []);
      const cards = facilityCards.filter(f => ccnSet.has(f.ccn) && !assigned.has(f.ccn));
      if (cards.length > 1) {
        const cardCcns = new Set(cards.map(c => c.ccn));
        cards.forEach(c => assigned.add(c.ccn));
        // Find tier-1 owners whose CCNs overlap with this sub-group's cards
        // Exclude the lead owner (owns all facilities) and the holding co itself
        const tier1Owners = tier1Members
          .filter((m: any) => {
            if (m.owner_name === hc.owner_name) return false;
            if (m.num_facilities === group.num_facilities) return false; // lead owner spans all
            const mCcns: string[] = m.ccn_list || [];
            return mCcns.some((c: string) => cardCcns.has(c));
          })
          .map((m: any) => ({
            name: m.owner_name as string,
            type: m.type_owner as string,
            count: ((m.ccn_list || []) as string[]).filter((c: string) => cardCcns.has(c)).length,
          }))
          .sort((a: any, b: any) => b.count - a.count);
        facilitySubGroups.push({ name: hc.owner_name, ccns: ccnSet, cards, tier1Owners });
      }
    }
    // Sort sub-groups by total MDS cost descending
    facilitySubGroups.sort((a, b) => {
      const aTotal = a.cards.reduce((s, f) => s + f.totalMdsCost, 0);
      const bTotal = b.cards.reduce((s, f) => s + f.totalMdsCost, 0);
      return bTotal - aTotal;
    });
    // Remaining facilities not in any sub-group
    const remaining = facilityCards.filter(f => !assigned.has(f.ccn));
    if (remaining.length > 0 && facilitySubGroups.length > 0) {
      facilitySubGroups.push({ name: 'Other Facilities', ccns: new Set(), cards: remaining, tier1Owners: [] });
    }
  }

  const stateRollup = states[0];
  const numFacilities = isStateScoped ? (stateRollup?.num_facilities || facilityCards.length) : group.num_facilities;

  // Primary owners: tier-1 members owning >50% of the group's facilities (excluding lead)
  // When state-scoped, count only CCNs that overlap with the scoped facilities
  const scopedCcns = isStateScoped ? new Set(facilityCards.map(f => f.ccn)) : null;
  const primaryOwners = members
    .filter((m: any) => m.tier === 1 && m.owner_name && m.owner_name !== group.group_name)
    .map((m: any) => {
      const memberCcns: string[] = m.ccn_list || [];
      const count = scopedCcns ? memberCcns.filter((c: string) => scopedCcns.has(c)).length : m.num_facilities;
      return { name: m.owner_name as string, type: m.type_owner as string, count };
    })
    .filter((o: any) => o.count >= numFacilities * 0.75)
    .sort((a: any, b: any) => b.count - a.count);
  // Deduplicate by name (a member can appear at multiple tiers)
  const seenNames = new Set<string>([group.group_name]);
  const uniquePrimaryOwners = primaryOwners.filter((o: any) => {
    if (seenNames.has(o.name)) return false;
    seenNames.add(o.name);
    return true;
  });
  // Compute mixed type from primary owners + lead
  const allOwnerTypes = new Set([group.lead_type_owner, ...uniquePrimaryOwners.map((o: any) => o.type)].filter(Boolean));
  const isMixedType = allOwnerTypes.has('I') && allOwnerTypes.has('O');

  // Compute group-level average risk score from facility cards
  const facsWithRisk = facilityCards.filter(f => f.riskScore != null);
  const avgRiskScore = facsWithRisk.length > 0
    ? Math.round(facsWithRisk.reduce((s, f) => s + f.riskScore!, 0) / facsWithRisk.length * 10) / 10
    : null;
  const avgRiskLevel = avgRiskScore != null ? getRiskLabel(avgRiskScore) : null;
  const avgRiskColor = avgRiskScore != null ? getRiskColor(avgRiskScore) : '#999';

  // Average factor scores from top_risk_factors JSON across facilities
  const avgFactorScores: Record<number, { total: number; count: number }> = {};
  for (const fac of facsWithRisk) {
    for (const rf of fac.riskFactors) {
      if (rf.factor == null || rf.score == null) continue;
      if (!avgFactorScores[rf.factor]) avgFactorScores[rf.factor] = { total: 0, count: 0 };
      avgFactorScores[rf.factor].total += rf.score;
      avgFactorScores[rf.factor].count += 1;
    }
  }

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(backUrl)} sx={{ mb: 1.5, ml: 1 }} size="small">
          {backLabel}
        </Button>

        <Paper sx={{ width: '100%' }}>
          {/* Header */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography variant="h5" fontWeight={700}>{group.group_name}</Typography>
                  {avgRiskScore != null && (
                    <Tooltip title="Click to see risk score breakdown" arrow>
                      <Box
                        onClick={() => setRiskModalOpen(true)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          cursor: 'pointer',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          backgroundColor: `${avgRiskColor}14`,
                          border: '1px solid',
                          borderColor: `${avgRiskColor}40`,
                          '&:hover': { backgroundColor: `${avgRiskColor}22` },
                          transition: 'background-color 0.2s',
                        }}
                      >
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: avgRiskColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem', boxShadow: `0 2px 8px ${avgRiskColor}66` }}>
                          {Math.round(avgRiskScore)}
                        </Box>
                        <Typography variant="caption" fontWeight={600} sx={{ color: avgRiskColor, textTransform: 'capitalize' }}>
                          {avgRiskLevel} Risk
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
                {uniquePrimaryOwners.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    {uniquePrimaryOwners.slice(0, 4).map((o: any) => (
                      <Typography key={o.name} variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        {o.name}
                        {o.count < numFacilities && (
                          <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                            ({o.count} of {numFacilities})
                          </Typography>
                        )}
                      </Typography>
                    ))}
                    {uniquePrimaryOwners.length > 4 && (
                      <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.75rem', fontStyle: 'italic', lineHeight: 1.4 }}>
                        +{uniquePrimaryOwners.length - 4} more
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Box display="flex" gap={1} alignItems="center" sx={{ mt: 0.5 }}>
                {(() => {
                  let ratingSum = 0, ratingCount = 0;
                  states.forEach((s: any) => {
                    if (s.avg_overall_rating != null) {
                      ratingSum += s.avg_overall_rating * s.num_facilities;
                      ratingCount += s.num_facilities;
                    }
                  });
                  const avgRating = ratingCount > 0 ? ratingSum / ratingCount : null;
                  return avgRating != null ? (
                    <Chip
                      label={`\u2605 ${avgRating.toFixed(1)} CMS Rating`}
                      size="medium" variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#ed6c02', borderColor: '#ed6c02' }}
                    />
                  ) : null;
                })()}
                {fromState && (
                  <Chip label={fromState} size="medium" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
                )}
                {!fromState && (
                  <Chip label={`${group.num_states} ${group.num_states === 1 ? 'State' : 'States'}`} size="medium" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
                )}
                <Chip
                  label={`${numFacilities} ${numFacilities === 1 ? 'Facility' : 'Facilities'}`}
                  size="medium" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                />
              </Box>
            </Box>
          </Box>

          {/* Tabs */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}>
            <Tabs
              value={tabValue <= 2 ? tabValue : false}
              onChange={(_e, v) => setTabValue(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                '& .MuiTabs-indicator': { height: 3 },
                '& .MuiTab-root': {
                  minHeight: 56,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  px: 3,
                  mx: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: '8px 8px 0 0',
                  },
                },
              }}
            >
              <Tab label="Facilities" />
              <Tab label="Related Parties" sx={{ position: 'relative', '&::after': { content: '"beta"', position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', fontSize: '0.5rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.6 } }} />
              <Tab label="Case Action" />
            </Tabs>
            <Box sx={{ flexGrow: 1 }} />
            <Tab
              label="Owners"
              onClick={() => setTabValue(3)}
              sx={{
                minHeight: 56,
                fontSize: '0.95rem',
                fontWeight: 500,
                textTransform: 'none',
                px: 3,
                mx: 0.5,
                opacity: tabValue === 3 ? 1 : 0.7,
                color: tabValue === 3 ? 'primary.main' : 'text.secondary',
                borderBottom: tabValue === 3 ? '3px solid' : '3px solid transparent',
                borderColor: tabValue === 3 ? 'primary.main' : 'transparent',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: '8px 8px 0 0',
                },
              }}
            />
          </Box>

          {/* Tab: Facilities */}
          {tabValue === 0 && (
            <Box sx={{ px: 3, py: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Based on CMS PBJ staffing and MDS acuity data, 2020–2025</Typography>
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Box onClick={() => setFacilitiesModel('hybrid')} sx={{ px: 1.5, py: 0.5, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, backgroundColor: isHybridModel ? '#1565c0' : 'transparent', color: isHybridModel ? 'white' : 'text.secondary' }}>Hybrid</Box>
                  <Box onClick={() => setFacilitiesModel('anticipated')} sx={{ px: 1.5, py: 0.5, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, backgroundColor: !isHybridModel ? '#1565c0' : 'transparent', color: !isHybridModel ? 'white' : 'text.secondary' }}>Anticipated</Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>% Days Failing to Staff to Acuity<br />({modelLabel})<br />Total Avg Across Facilities</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color: summaryDaysFailing != null && summaryDaysFailing > 50 ? 'error.main' : 'text.primary' }}>{summaryDaysFailing != null ? `${summaryDaysFailing.toFixed(1)}%` : '-'}</Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>Projected Total Understaffing<br />({modelLabel}, No Credit)<br />Sum Across Facilities</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>{formatMoney(summaryCostNoCredit)}</Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>Projected Total Understaffing<br />({modelLabel}, w/ Credit)<br />Sum Across Facilities</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>{formatMoney(Math.max(0, summaryCostWithCredit || 0))}</Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.3, minHeight: 28 }}>% Days Meeting {fromState || 'State'}<br />State Staffing Requirement<br />Total Avg Across Facilities</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>{totals.pct_days_meeting_state_min != null ? `${totals.pct_days_meeting_state_min.toFixed(1)}%` : '-'}</Typography>
                </Box>
              </Box>
              {isStateScoped ? (
                <>
                  {facilityCards.map((fac) => (
                    <FacilityCard key={fac.ccn} fac={fac} schemeId={schemeId!} navigate={navigate} stateAcuity={stateAcuity} stateAcuityByYear={stateAcuityByYear} model={facilitiesModel} />
                  ))}
                  {facilityCards.length === 0 && (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No facility data available.</Typography>
                  )}
                </>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#e0e0e0', '& th': { fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.25 } }}>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell>State</TableCell>
                        <TableCell align="right"># Facilities</TableCell>
                        <TableCell align="right">Anticipated w/ Staffing Credit</TableCell>
                        <TableCell align="right">Anticipated No Staffing Credit</TableCell>
                        <TableCell align="right">Hybrid w/ Staffing Credit</TableCell>
                        <TableCell align="right">Hybrid No Staffing Credit</TableCell>
                        <TableCell align="right">RP Paid</TableCell>
                        <TableCell align="right">Harm Cites</TableCell>
                        <TableCell align="right">Avg Rating</TableCell>
                        <TableCell align="right">Fines</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {states.map((s) => (
                        <StateRow key={s.state} stateData={s} navigate={navigate} />
                      ))}
                      {states.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11}>
                            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No state data available.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Tab: Related Parties */}
          {tabValue === 1 && (
            <Box sx={{ px: 3, py: 3 }}>
              <Tabs
                value={rpSubTab}
                onChange={(_e, v) => setRpSubTab(v)}
                sx={{
                  mb: 2,
                  minHeight: 36,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '& .MuiTabs-indicator': { height: 2 },
                  '& .MuiTab-root': { minHeight: 36, fontSize: '0.85rem', textTransform: 'none', fontWeight: 500, px: 2 },
                }}
              >
                <Tab label="Summary" />
                <Tab label="Categories of Related Parties" />
                <Tab label="Additional Related Party Analysis" />
                <Tab label="Other Flagged Transactions" />
                <Tab label="Cost Report Analysis" />
              </Tabs>

              {rpSubTab === 0 && (
              <Box sx={{ py: 2 }}>
              {rpLoading ? (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
              ) : !rpData || (rpData.total_rp_total_paid ?? 0) <= 0 ? (
                <Box sx={{ p: 2.5, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">
                    No related party payments reported across facilities in this owner group.
                  </Typography>
                </Box>
              ) : (() => {
                const rpPerDay = rpData.rp_per_resident_day;
                const rpPct = rpData.rp_pct_gross_rev;
                const stateBm = fromState ? rpData.rp_benchmarks?.by_state?.[fromState] : null;
                const natBm = rpData.rp_benchmarks?.national;

                // Per-resident-day comparison bars
                const isAboveStDay = rpPerDay != null && stateBm?.median_rp_per_resident_day != null && rpPerDay > stateBm.median_rp_per_resident_day;
                const dayColor = isAboveStDay ? '#d32f2f' : '#2e7d32';
                const dayItems = [
                  ...(rpPerDay != null ? [{ label: 'This Owner Group', value: rpPerDay, color: dayColor, bold: true }] : []),
                  ...(stateBm?.median_rp_per_resident_day != null ? [{ label: `${fromState} Median`, value: stateBm.median_rp_per_resident_day, color: '#90caf9', bold: false }] : []),
                  ...(natBm?.median_rp_per_resident_day != null ? [{ label: 'National Median', value: natBm.median_rp_per_resident_day, color: '#90caf9', bold: false }] : []),
                ].sort((a, b) => b.value - a.value);
                const dayMax = Math.max(...dayItems.map(i => i.value), 1) * 1.1;

                // % of revenue comparison bars
                const isAboveStPct = rpPct != null && stateBm?.median_rp_pct != null && rpPct > stateBm.median_rp_pct;
                const pctColor = isAboveStPct ? '#d32f2f' : '#2e7d32';
                const pctItems = [
                  ...(rpPct != null ? [{ label: 'This Owner Group', value: rpPct, color: pctColor, bold: true }] : []),
                  ...(natBm?.median_rp_pct != null ? [{ label: 'National Median', value: natBm.median_rp_pct, color: '#90caf9', bold: false }] : []),
                  ...(stateBm?.median_rp_pct != null ? [{ label: `${fromState} Median`, value: stateBm.median_rp_pct, color: '#90caf9', bold: false }] : []),
                ].sort((a, b) => b.value - a.value);
                const pctMax = Math.max(...pctItems.map(i => i.value), 1) * 1.1;

                return (
                  <>
                    <Box mb={2}>
                      <Typography variant="h6" fontWeight={700}>Related Party Payments</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total 2020–2024 from Medicare cost reports — aggregated across {rpData.facilities_with_rp} {rpData.facilities_with_rp === 1 ? 'facility' : 'facilities'}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={2} mb={3}>
                      {/* Card 1: Total RP Payments */}
                      <Box sx={{ flex: 1, p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                          Total Related Party Payments
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {formatMoneyFull(rpData.total_rp_total_paid)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">2020–2024</Typography>
                      </Box>

                      {/* Card 2: Excess Over Allowable */}
                      <Box sx={{ flex: 1, p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                          Payments in Excess of Allowable
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {(rpData.total_rp_total_adj ?? 0) < 0 ? formatMoneyFull(Math.abs(rpData.total_rp_total_adj)) : '$0'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">2020–2024</Typography>
                      </Box>

                      {/* Card 3: RP Per Resident Day */}
                      <Box sx={{ flex: 1.5, p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                          RP Payments Per Resident Day
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {rpPerDay != null ? `$${rpPerDay.toFixed(2)}` : '-'}
                          <Typography component="span" variant="caption" color="text.secondary">/day</Typography>
                        </Typography>
                        {dayItems.length > 1 && (
                          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {dayItems.map((item, idx) => {
                              const barW = (item.value / dayMax) * 100;
                              return (
                                <Box key={idx} sx={{ textAlign: 'left' }}>
                                  <Box display="flex" justifyContent="space-between" mb={0.15}>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: item.bold ? 700 : 500, color: item.bold ? item.color : 'text.secondary' }}>{item.label}</Typography>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: item.bold ? 700 : 600, color: item.bold ? item.color : 'text.secondary' }}>${item.value.toFixed(2)}/day</Typography>
                                  </Box>
                                  <Box sx={{ height: item.bold ? 10 : 6, backgroundColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${barW}%`, backgroundColor: item.color, borderRadius: 1 }} />
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>

                      {/* Card 4: RP as % of Revenue */}
                      <Box sx={{ flex: 1.5, p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                        <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                          RP Payments as % of Revenue
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {rpPct != null ? `${rpPct.toFixed(1)}%` : '-'}
                        </Typography>
                        {pctItems.length > 1 && (
                          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {pctItems.map((item, idx) => {
                              const barW = (item.value / pctMax) * 100;
                              return (
                                <Box key={idx} sx={{ textAlign: 'left' }}>
                                  <Box display="flex" justifyContent="space-between" mb={0.15}>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: item.bold ? 700 : 500, color: item.bold ? item.color : 'text.secondary' }}>{item.label}</Typography>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: item.bold ? 700 : 600, color: item.bold ? item.color : 'text.secondary' }}>{item.value.toFixed(1)}%</Typography>
                                  </Box>
                                  <Box sx={{ height: item.bold ? 10 : 6, backgroundColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${barW}%`, backgroundColor: item.color, borderRadius: 1 }} />
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* RP Entities × Facility Grid (same layout as CheckEngineGrid) */}
                    {rpData.rp_entities?.length > 0 && rpData.facilities?.length > 0 && (() => {
                      const facs: { ccn: string; name: string }[] = rpData.facilities;
                      const entities: any[] = [...rpData.rp_entities].sort((a: any, b: any) =>
                        (a.entity_name || '').localeCompare(b.entity_name || '', undefined, { sensitivity: 'base' }));
                      // Short names for column headers
                      const shortName = (f: any) => {
                        let n = (f.name || '') as string;
                        n = n.replace(/,?\s*(A\s+)?\w+\s+(CENTER|CENTRE|FACILITY).*$/i, '');
                        n = n.replace(/\s+(LLC|INC|CORP)\.?$/i, '');
                        return n.trim() || f.ccn;
                      };

                      const labelW = 250;
                      const totalW = 50;
                      const facColW = 90;
                      const gridW = labelW + totalW + facs.length * facColW;
                      const cols = `${labelW}px ${totalW}px repeat(${facs.length}, ${facColW}px)`;

                      return (
                        <Box mt={3}>
                          <Typography variant="h6" fontWeight={700} mb={0.5}>Related Party Entities</Typography>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Related Party Entities appearing in Medicare cost reports (Worksheet A-8-1 Part II) across {facs.length} facilities
                          </Typography>
                          <Typography variant="caption" color="text.disabled" display="block" mb={2} sx={{ fontStyle: 'italic', fontSize: '0.65rem' }}>
                            * The same organization can appear under several spellings or abbreviations across different cost reports.
                          </Typography>
                          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto', maxHeight: 700 }}>
                            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: gridW }}>
                              <thead>
                                <tr>
                                  <th style={{
                                    position: 'sticky', left: 0, top: 0, zIndex: 3,
                                    background: '#f5f5f5', minWidth: labelW, maxWidth: labelW,
                                    padding: '8px 12px', textAlign: 'left',
                                    borderBottom: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                                    fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                                  }}>
                                    Organization
                                  </th>
                                  <th style={{
                                    position: 'sticky', left: labelW, top: 0, zIndex: 3,
                                    background: '#f5f5f5', minWidth: totalW, width: totalW,
                                    padding: '8px 4px', textAlign: 'center',
                                    borderBottom: '2px solid #e0e0e0', borderRight: '2px solid #e0e0e0',
                                    fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                                  }}>
                                    Total
                                  </th>
                                  {facs.map((f: any) => (
                                    <Tooltip key={f.ccn} title={f.name} arrow>
                                      <th style={{
                                        position: 'sticky', top: 0, zIndex: 2,
                                        background: '#f5f5f5', minWidth: facColW, width: facColW,
                                        padding: '6px 4px', textAlign: 'center',
                                        borderBottom: '2px solid #e0e0e0', cursor: 'pointer',
                                      }}>
                                        <a
                                          href={`/schemes/snf-understaffing/facility/${f.ccn}?tab=related_parties`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ textDecoration: 'none', color: 'inherit' }}
                                        >
                                          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', lineHeight: 1.3, display: 'block', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                                            {f.ccn}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{
                                            fontSize: '0.65rem', lineHeight: 1.2, display: 'block',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                            maxWidth: facColW - 8, mx: 'auto',
                                          }}>
                                            {shortName(f)}
                                          </Typography>
                                        </a>
                                      </th>
                                    </Tooltip>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {entities.map((ent: any, idx: number) => {
                                  const ccnSet = new Set(ent.ccns);
                                  return (
                                    <tr key={idx} style={{ borderBottom: idx < entities.length - 1 ? '1px solid #f0f0f0' : undefined }}
                                      onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                                    >
                                      <td style={{
                                        position: 'sticky', left: 0, zIndex: 1,
                                        background: '#fff', minWidth: labelW, maxWidth: labelW,
                                        padding: '5px 12px',
                                        borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #f0f0f0',
                                      }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {ent.entity_name}
                                        </Typography>
                                      </td>
                                      <td style={{
                                        position: 'sticky', left: labelW, zIndex: 1,
                                        background: '#fff', minWidth: totalW, width: totalW,
                                        padding: '5px 4px', textAlign: 'center',
                                        borderRight: '2px solid #e0e0e0', borderBottom: '1px solid #f0f0f0',
                                      }}>
                                        <Typography variant="caption" fontWeight={700} sx={{ color: ent.count > 1 ? '#1565c0' : 'text.disabled', fontSize: '0.7rem' }}>
                                          {ent.count > 0 ? ent.count : '—'}
                                        </Typography>
                                      </td>
                                      {facs.map((f: any) => (
                                        <td key={f.ccn} style={{ padding: '5px 0', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                          {ccnSet.has(f.ccn) ? (
                                            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#1565c0', mx: 'auto', boxShadow: '0 1px 3px #1565c055' }} />
                                          ) : null}
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td style={{
                                    position: 'sticky', left: 0, zIndex: 1,
                                    background: '#f5f5f5', padding: '8px 12px',
                                    borderTop: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                                    fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                                  }}>
                                    Orgs per Facility
                                  </td>
                                  <td style={{
                                    position: 'sticky', left: labelW, zIndex: 1,
                                    background: '#f5f5f5', padding: '8px 4px',
                                    borderTop: '2px solid #e0e0e0', borderRight: '2px solid #e0e0e0',
                                  }} />
                                  {facs.map((f: any) => {
                                    const cnt = entities.filter((e: any) => e.ccns.includes(f.ccn)).length;
                                    return (
                                      <td key={f.ccn} style={{ background: '#f5f5f5', padding: '8px 0', textAlign: 'center', borderTop: '2px solid #e0e0e0' }}>
                                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', color: cnt > 0 ? '#1565c0' : 'text.disabled' }}>
                                          {cnt || '—'}
                                        </Typography>
                                      </td>
                                    );
                                  })}
                                </tr>
                              </tfoot>
                            </table>
                          </Paper>
                        </Box>
                      );
                    })()}
                  </>
                );
              })()}
            </Box>
              )}

              {/* Sub-tab: Categories of Related Parties */}
              {rpSubTab === 1 && (
                <Box sx={{ py: 2 }}>
                  {rpLoading ? (
                    <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                  ) : !rpData?.cost_center_matrix?.length ? (
                    <Box sx={{ p: 2.5, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">No cost center data available.</Typography>
                    </Box>
                  ) : (() => {
                    const facs: { ccn: string; name: string }[] = rpData.facilities || [];
                    const matrix: any[] = rpData.cost_center_matrix;
                    const shortName = (f: any) => {
                      let n = (f.name || '') as string;
                      n = n.replace(/,?\s*(A\s+)?\w+\s+(CENTER|CENTRE|FACILITY).*$/i, '');
                      n = n.replace(/\s+(LLC|INC|CORP)\.?$/i, '');
                      return n.trim() || f.ccn;
                    };
                    const labelW = 250;
                    const totalW = 110;
                    const facColW = 110;

                    return (
                      <Box>
                        <Typography variant="h6" fontWeight={700} mb={0.5}>Related Party Payments by Cost Center</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                          Total related party payments (2020–2024) by cost center across {facs.length} facilities
                        </Typography>
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto', maxHeight: 700 }}>
                          <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: labelW + totalW + facs.length * facColW }}>
                            <thead>
                              <tr>
                                <th style={{
                                  position: 'sticky', left: 0, top: 0, zIndex: 3,
                                  background: '#f5f5f5', minWidth: labelW, maxWidth: labelW,
                                  padding: '8px 12px', textAlign: 'left',
                                  borderBottom: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                                  fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                                }}>
                                  Cost Center
                                </th>
                                <th style={{
                                  position: 'sticky', left: labelW, top: 0, zIndex: 3,
                                  background: '#f5f5f5', minWidth: totalW, width: totalW,
                                  padding: '8px 8px', textAlign: 'right',
                                  borderBottom: '2px solid #e0e0e0', borderRight: '2px solid #e0e0e0',
                                  fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                                }}>
                                  Total
                                </th>
                                {facs.map((f: any) => (
                                  <Tooltip key={f.ccn} title={f.name} arrow>
                                    <th style={{
                                      position: 'sticky', top: 0, zIndex: 2,
                                      background: '#f5f5f5', minWidth: facColW, width: facColW,
                                      padding: '6px 4px', textAlign: 'center',
                                      borderBottom: '2px solid #e0e0e0', cursor: 'pointer',
                                    }}>
                                      <a
                                        href={`/schemes/snf-understaffing/facility/${f.ccn}?tab=related_parties`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                      >
                                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', lineHeight: 1.3, display: 'block', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                                          {f.ccn}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                          fontSize: '0.65rem', lineHeight: 1.2, display: 'block',
                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                          maxWidth: facColW - 8, mx: 'auto',
                                        }}>
                                          {shortName(f)}
                                        </Typography>
                                      </a>
                                    </th>
                                  </Tooltip>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {matrix.map((row: any, idx: number) => (
                                <tr key={idx}
                                  onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                                >
                                  <td style={{
                                    position: 'sticky', left: 0, zIndex: 1,
                                    background: '#fff', minWidth: labelW, maxWidth: labelW,
                                    padding: '6px 12px',
                                    borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #f0f0f0',
                                  }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                      {row.cost_center}
                                    </Typography>
                                  </td>
                                  <td style={{
                                    position: 'sticky', left: labelW, zIndex: 1,
                                    background: '#fff', minWidth: totalW, width: totalW,
                                    padding: '6px 8px', textAlign: 'right',
                                    borderRight: '2px solid #e0e0e0', borderBottom: '1px solid #f0f0f0',
                                  }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', color: '#1565c0' }}>
                                      {formatMoney(row.total)}
                                    </Typography>
                                  </td>
                                  {facs.map((f: any) => {
                                    const val = row.by_facility?.[f.ccn];
                                    return (
                                      <td key={f.ccn} style={{ padding: '6px 4px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                        {val ? (
                                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                            {formatMoney(val)}
                                          </Typography>
                                        ) : null}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td style={{
                                  position: 'sticky', left: 0, zIndex: 1,
                                  background: '#f5f5f5', padding: '8px 12px',
                                  borderTop: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                                  fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                                }}>
                                  Total
                                </td>
                                <td style={{
                                  position: 'sticky', left: labelW, zIndex: 1,
                                  background: '#f5f5f5', padding: '8px 8px', textAlign: 'right',
                                  borderTop: '2px solid #e0e0e0', borderRight: '2px solid #e0e0e0',
                                }}>
                                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', color: '#1565c0' }}>
                                    {formatMoney(matrix.reduce((s: number, r: any) => s + (r.total || 0), 0))}
                                  </Typography>
                                </td>
                                {facs.map((f: any) => {
                                  const facTotal = matrix.reduce((s: number, r: any) => s + (r.by_facility?.[f.ccn] || 0), 0);
                                  return (
                                    <td key={f.ccn} style={{ background: '#f5f5f5', padding: '8px 4px', textAlign: 'center', borderTop: '2px solid #e0e0e0' }}>
                                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', color: facTotal > 0 ? '#1565c0' : 'text.disabled' }}>
                                        {facTotal > 0 ? formatMoney(facTotal) : '—'}
                                      </Typography>
                                    </td>
                                  );
                                })}
                              </tr>
                            </tfoot>
                          </table>
                        </Paper>
                      </Box>
                    );
                  })()}
                </Box>
              )}

              {/* Sub-tabs: Additional Related Party Analysis & Other Flagged Transactions */}
              {(rpSubTab === 2 || rpSubTab === 3) && (
                <Box sx={{ py: 3 }}>
                  {ceLoading ? (
                    <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                  ) : !ceData || !ceData.signals?.length ? (
                    <Box sx={{ p: 2.5, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">
                        No signals available for this owner group.
                      </Typography>
                    </Box>
                  ) : (() => {
                    const ceFacs: { ccn: string; name: string }[] = ceData.facilities;
                    const ceShortName = (f: any) => {
                      let n = (f.name || '') as string;
                      n = n.replace(/,?\s*(A\s+)?\w+\s+(CENTER|CENTRE|FACILITY).*$/i, '');
                      n = n.replace(/\s+(LLC|INC|CORP)\.?$/i, '');
                      return n.trim() || f.ccn;
                    };

                    const CAT_COLORS: Record<string, string> = {
                      'Declared RP': '#00695c', 'Hidden Related Party': '#1565c0',
                      'RP Year-over-Year': '#6a1b9a', 'Balance Sheet': '#e65100',
                      'Balance Sheet YoY': '#e65100', 'Staffing Integrity': '#2e7d32',
                      'Billing & Revenue': '#ad1457',
                    };

                    // Filter + group signals based on active sub-tab
                    type SectionDef = { label: string; categories: string[]; color: string };
                    let sections: SectionDef[];
                    if (rpSubTab === 2) {
                      sections = [
                        { label: 'Section A: Declared Related Parties', categories: ['Declared RP'], color: '#00695c' },
                        { label: 'Section B: Hidden Related Parties', categories: ['Hidden Related Party'], color: '#1565c0' },
                      ];
                    } else {
                      sections = [
                        { label: 'Section A: Staffing Flags', categories: ['Staffing Integrity'], color: '#00695c' },
                        { label: 'Section B: Billing & Revenue Flags', categories: ['Billing & Revenue'], color: '#1565c0' },
                        { label: 'Section C: Balance Sheet Flags', categories: ['Balance Sheet YoY'], color: '#e65100' },
                      ];
                    }

                    // Remap contract_labor from Staffing Integrity to Hidden Related Party; hide cr_vs_pbj
                    const remappedSignals = (ceData.signals as any[])
                      .filter((s: any) => s.signal_id !== 'cr_vs_pbj')
                      .map((s: any) =>
                        s.signal_id === 'contract_labor' ? { ...s, category: 'Hidden Related Party' } : s
                      );

                    const allCats = sections.flatMap(s => s.categories);
                    const filteredSignals = remappedSignals.filter((s: any) => allCats.includes(s.category));

                    // Add placeholder rows for signals in SIGNAL_ORDER that aren't in the API response
                    const existingIds = new Set(filteredSignals.map((s: any) => s.signal_id));
                    // Build a category map with the contract_labor remap applied
                    const catMap: Record<string, string> = { ...SIGNAL_CATEGORY, contract_labor: 'Hidden Related Party' };
                    for (const sigId of SIGNAL_ORDER) {
                      if (existingIds.has(sigId)) continue;
                      if (sigId === 'cr_vs_pbj') continue;
                      const cat = catMap[sigId];
                      if (!cat || !allCats.includes(cat)) continue;
                      filteredSignals.push({ signal_id: sigId, category: cat, scores: {} });
                    }

                    const grouped = sections
                      .map(sec => ({
                        label: sec.label,
                        color: sec.color,
                        sigs: filteredSignals
                          .filter((s: any) => sec.categories.includes(s.category))
                          .sort((a: any, b: any) => {
                            const ai = SIGNAL_ORDER.indexOf(a.signal_id);
                            const bi = SIGNAL_ORDER.indexOf(b.signal_id);
                            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                          }),
                      }))
                      ;

                    // Compute row counts for BOTH tabs to set a consistent minHeight
                    // Build full signal lists (with placeholders) for both tabs to get accurate counts
                    const rpCats = ['Declared RP', 'Hidden Related Party'];
                    const otherCats = ['Staffing Integrity', 'Billing & Revenue', 'Balance Sheet YoY'];
                    const allRemappedIds = new Set(remappedSignals.map((s: any) => s.signal_id));
                    const countSignalsForCats = (cats: string[]) => {
                      let count = remappedSignals.filter((s: any) => cats.includes(s.category)).length;
                      for (const sigId of SIGNAL_ORDER) {
                        if (allRemappedIds.has(sigId) || sigId === 'cr_vs_pbj') continue;
                        const cat = catMap[sigId];
                        if (cat && cats.includes(cat)) count++;
                      }
                      return count;
                    };
                    const rpSignalCount = countSignalsForCats(rpCats);
                    const otherSignalCount = countSignalsForCats(otherCats);
                    const rpSectionCount = rpCats.length;
                    const otherSectionCount = otherCats.length;
                    // ~45px per signal row, ~40px per section header, ~50px header, ~45px footer
                    const rpHeight = 95 + rpSectionCount * 40 + rpSignalCount * 45;
                    const otherHeight = 95 + otherSectionCount * 40 + otherSignalCount * 45;
                    const tableMinHeight = Math.max(rpHeight, otherHeight);

                    const ceLabelW = 350;
                    const ceTotalW = 50;
                    const ceAvgW = 50;
                    const ceFacColW = 90;
                    const numFacs = ceFacs.length;

                    return (
                      <>
                      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
                        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          Each signal is scored per facility per year: <strong style={{ color: '#d32f2f' }}>Red = 2 pts</strong> &middot; <strong style={{ color: '#f9a825' }}>Yellow = 1 pt</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <strong>Total</strong> &mdash; sum of points across all facilities
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <strong>Avg</strong> &mdash; total &divide; number of facilities
                        </Typography>
                      </Box>

                      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto', minHeight: tableMinHeight }}>
                        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: ceLabelW + ceTotalW + ceAvgW + numFacs * ceFacColW }}>
                          <thead>
                            <tr>
                              <th style={{
                                position: 'sticky', left: 0, top: 0, zIndex: 3,
                                background: '#f5f5f5', minWidth: ceLabelW, maxWidth: ceLabelW,
                                padding: '8px 12px', textAlign: 'left',
                                borderBottom: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                                fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                              }}>
                                Signal
                              </th>
                              <th style={{
                                position: 'sticky', left: ceLabelW, top: 0, zIndex: 3,
                                background: '#f5f5f5', minWidth: ceTotalW, width: ceTotalW,
                                padding: '8px 4px', textAlign: 'center',
                                borderBottom: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                                fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                              }}>
                                Total
                              </th>
                              <th style={{
                                position: 'sticky', left: ceLabelW + ceTotalW, top: 0, zIndex: 3,
                                background: '#f5f5f5', minWidth: ceAvgW, width: ceAvgW,
                                padding: '8px 4px', textAlign: 'center',
                                borderBottom: '2px solid #e0e0e0', borderRight: '2px solid #e0e0e0',
                                fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                              }}>
                                Avg
                              </th>
                              {ceFacs.map((f: any) => (
                                <Tooltip key={f.ccn} title={f.name} arrow>
                                  <th style={{
                                    position: 'sticky', top: 0, zIndex: 2,
                                    background: '#f5f5f5', minWidth: ceFacColW, width: ceFacColW,
                                    padding: '6px 4px', textAlign: 'center',
                                    borderBottom: '2px solid #e0e0e0', cursor: 'pointer',
                                  }}>
                                    <a href={`/schemes/snf-understaffing/facility/${f.ccn}?tab=related_parties`}
                                      target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', lineHeight: 1.3, display: 'block', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                                        {f.ccn}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{
                                        fontSize: '0.65rem', lineHeight: 1.2, display: 'block',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        maxWidth: ceFacColW - 8, mx: 'auto',
                                      }}>
                                        {ceShortName(f)}
                                      </Typography>
                                    </a>
                                  </th>
                                </Tooltip>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {grouped.map(({ label, color, sigs }) => (
                              <React.Fragment key={label}>
                                {(() => {
                                  // Blend hex color at ~7% opacity onto white for an opaque equivalent
                                  const h = color.replace('#', '');
                                  const a = 0x12 / 255;
                                  const bg = `rgb(${Math.round(parseInt(h.substring(0,2),16)*a+255*(1-a))},${Math.round(parseInt(h.substring(2,4),16)*a+255*(1-a))},${Math.round(parseInt(h.substring(4,6),16)*a+255*(1-a))})`;
                                  return (
                                    <tr>
                                      <td style={{
                                        position: 'sticky', left: 0, zIndex: 2,
                                        background: bg,
                                        padding: '8px 12px', borderBottom: `2px solid ${color}40`, borderTop: `2px solid ${color}40`,
                                      }} colSpan={3}>
                                        <Typography variant="subtitle2" sx={{
                                          color, fontWeight: 800, letterSpacing: 0.5,
                                        }}>
                                          {label}
                                        </Typography>
                                      </td>
                                      {ceFacs.map((f: any) => (
                                        <td key={f.ccn} style={{
                                          background: bg,
                                          borderBottom: `2px solid ${color}40`, borderTop: `2px solid ${color}40`,
                                        }} />
                                      ))}
                                    </tr>
                                  );
                                })()}
                                {sigs.map((sig: any, sIdx: number) => {
                                  const totalScore = Object.values(sig.scores || {}).reduce((sum: number, sc: any) => sum + sc.score, 0) as number;
                                  return (
                                    <tr key={sig.signal_id}
                                      onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                                    >
                                      <td style={{
                                        position: 'sticky', left: 0, zIndex: 1,
                                        background: '#fff', minWidth: ceLabelW, maxWidth: ceLabelW,
                                        padding: '5px 12px', borderRight: '1px solid #e0e0e0',
                                        borderBottom: sIdx < sigs.length - 1 ? '1px solid #f0f0f0' : 'none',
                                      }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                          {SIGNAL_METADATA[sig.signal_id]?.name || sig.signal_id}
                                        </Typography>
                                        {SIGNAL_METADATA[sig.signal_id]?.description && (
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.3, display: 'block' }}>
                                            {SIGNAL_METADATA[sig.signal_id].description}
                                          </Typography>
                                        )}
                                      </td>
                                      <td style={{
                                        position: 'sticky', left: ceLabelW, zIndex: 1,
                                        background: '#fff', minWidth: ceTotalW, width: ceTotalW,
                                        padding: '5px 4px', textAlign: 'center',
                                        borderRight: '1px solid #e0e0e0',
                                        borderBottom: sIdx < sigs.length - 1 ? '1px solid #f0f0f0' : 'none',
                                      }}>
                                        <Typography variant="caption" fontWeight={700} sx={{
                                          color: totalScore > 0 ? '#d32f2f' : 'text.disabled', fontSize: '0.7rem',
                                        }}>
                                          {totalScore > 0 ? totalScore : '—'}
                                        </Typography>
                                      </td>
                                      <td style={{
                                        position: 'sticky', left: ceLabelW + ceTotalW, zIndex: 1,
                                        background: '#fff', minWidth: ceAvgW, width: ceAvgW,
                                        padding: '5px 4px', textAlign: 'center',
                                        borderRight: '2px solid #e0e0e0',
                                        borderBottom: sIdx < sigs.length - 1 ? '1px solid #f0f0f0' : 'none',
                                      }}>
                                        <Typography variant="caption" fontWeight={700} sx={{
                                          color: totalScore > 0 ? '#d32f2f' : 'text.disabled', fontSize: '0.7rem',
                                        }}>
                                          {numFacs > 0 && totalScore > 0 ? (totalScore / numFacs).toFixed(1) : '—'}
                                        </Typography>
                                      </td>
                                      {ceFacs.map((f: any) => {
                                        const sc = sig.scores?.[f.ccn];
                                        if (!sc) return (
                                          <td key={f.ccn} style={{
                                            padding: '5px 0', textAlign: 'center',
                                            borderBottom: sIdx < sigs.length - 1 ? '1px solid #f0f0f0' : 'none',
                                          }}>
                                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>—</Typography>
                                          </td>
                                        );
                                        return (
                                          <td key={f.ccn} style={{
                                            padding: '5px 0', textAlign: 'center',
                                            borderBottom: sIdx < sigs.length - 1 ? '1px solid #f0f0f0' : 'none',
                                          }}>
                                            <Tooltip title={`${sc.reds} red × 2 + ${sc.yellows} yellow × 1 = ${sc.score}`} arrow>
                                              <Typography variant="caption" fontWeight={700} sx={{
                                                fontSize: '0.75rem',
                                                color: sc.score >= 6 ? '#d32f2f' : sc.score >= 3 ? '#e65100' : '#f9a825',
                                              }}>
                                                {sc.score}
                                              </Typography>
                                            </Tooltip>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td style={{
                                position: 'sticky', left: 0, bottom: 0, zIndex: 3,
                                background: '#f5f5f5', padding: '8px 12px',
                                borderTop: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                                fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                              }}>
                                Facility Totals
                              </td>
                              {(() => {
                                let grandTotal = 0;
                                filteredSignals.forEach((s: any) => {
                                  Object.values(s.scores || {}).forEach((sc: any) => { grandTotal += sc.score; });
                                });
                                return (
                                  <td style={{
                                    position: 'sticky', left: ceLabelW, bottom: 0, zIndex: 3,
                                    background: '#f5f5f5', padding: '8px 4px', textAlign: 'center',
                                    borderTop: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                                  }}>
                                    <Typography variant="caption" fontWeight={700} sx={{
                                      fontSize: '0.75rem',
                                      color: grandTotal > 0 ? '#d32f2f' : 'text.disabled',
                                    }}>
                                      {grandTotal > 0 ? grandTotal : '—'}
                                    </Typography>
                                  </td>
                                );
                              })()}
                              <td style={{
                                position: 'sticky', left: ceLabelW + ceTotalW, bottom: 0, zIndex: 3,
                                background: '#f5f5f5', padding: '8px 4px',
                                borderTop: '2px solid #e0e0e0', borderRight: '2px solid #e0e0e0',
                              }} />
                              {ceFacs.map((f: any) => {
                                let total = 0;
                                filteredSignals.forEach((s: any) => {
                                  const sc = s.scores?.[f.ccn];
                                  if (sc) total += sc.score;
                                });
                                return (
                                  <td key={f.ccn} style={{
                                    position: 'sticky', bottom: 0, zIndex: 1,
                                    background: '#f5f5f5', padding: '8px 0', textAlign: 'center',
                                    borderTop: '2px solid #e0e0e0',
                                  }}>
                                    <Typography variant="caption" fontWeight={700} sx={{
                                      fontSize: '0.75rem',
                                      color: total > 0 ? (total >= 20 ? '#d32f2f' : total >= 10 ? '#e65100' : '#1565c0') : 'text.disabled',
                                    }}>
                                      {total > 0 ? total : '—'}
                                    </Typography>
                                  </td>
                                );
                              })}
                            </tr>
                          </tfoot>
                        </table>
                      </Paper>
                      </>
                    );
                  })()}
                </Box>
              )}

              {/* Sub-tab: Cost Report Analysis (concentration / targeting) */}
              {rpSubTab === 4 && (
                <Box sx={{ py: 2 }}>
                  <OwnerGroupCostReportTab groupId={groupId!} state={fromState || undefined} schemeId={schemeId || undefined} />
                </Box>
              )}
            </Box>
          )}

          {/* Tab: Case Action */}
          {tabValue === 2 && (
            <Box sx={{ px: 3, py: 3 }}>
              <OwnerGroupCaseActionSection
                schemeId={schemeId}
                ownerGroupId={groupId}
                facilities={facilityCards.map(f => ({ ccn: f.ccn, facility_name: f.name })).sort((a, b) => a.facility_name.localeCompare(b.facility_name))}
              />
            </Box>
          )}

          {/* Tab: Owners */}
          {tabValue === 3 && (() => {
            const ROLE_LABELS: Record<string, string> = {
              '25': 'Contracted Managing Employee',
              '34': '5%+ Direct Ownership',
              '35': '5%+ Indirect Ownership',
              '36': '5%+ Mortgage Interest',
              '37': '5%+ Security Interest',
              '38': 'General Partnership Interest',
              '39': 'Limited Partnership Interest',
              '40': 'Officer',
              '41': 'Director',
              '42': 'W-2 Managing Employee',
              '43': 'Operational/Managerial Control',
              '44': 'Other',
              '63': 'Managing Control — Governing Body',
              '71': 'Trustee',
              '72': 'Authorized Designated Person',
              '85': 'Direct Interest',
              '86': 'Indirect Interest',
            };
            // Dedupe by owner_name — keep highest tier (lowest number)
            const deduped = new Map<string, any>();
            for (const m of members) {
              if (!m.owner_name) continue;
              const key = m.owner_name.trim().toUpperCase();
              const existing = deduped.get(key);
              if (!existing || (m.tier || 99) < (existing.tier || 99)) {
                // Merge role_codes from all tiers
                const mergedRoles = existing
                  ? Array.from(new Set([...(existing.role_codes || []), ...(m.role_codes || [])]))
                  : (m.role_codes || []);
                deduped.set(key, { ...m, role_codes: mergedRoles });
              } else if (existing) {
                existing.role_codes = Array.from(new Set([...(existing.role_codes || []), ...(m.role_codes || [])]));
              }
            }
            const allUniqueMembers = Array.from(deduped.values());
            // When state-scoped, filter out members with 0 facilities in scope
            const uniqueMembers = isStateScoped && facilityCards.length > 0
              ? allUniqueMembers.filter(m => {
                  const ccns: string[] = m.ccn_list || [];
                  return ccns.some((c: string) => facilityCards.some(f => f.ccn === c));
                })
              : allUniqueMembers;

            // Group by tier
            const tierSort = (a: any, b: any) => (b.num_facilities || 0) - (a.num_facilities || 0);
            const tier1 = uniqueMembers.filter(m => m.tier === 1).sort(tierSort);
            const tier2 = uniqueMembers.filter(m => m.tier === 2).sort(tierSort);
            const tier3 = uniqueMembers.filter(m => m.tier === 3).sort(tierSort);
            const tier4 = uniqueMembers.filter(m => m.tier === 4).sort(tierSort);

            const renderTierSection = (title: string, description: string, items: any[]) => items.length === 0 ? null : (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{description}</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5, py: 1 } }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell align="right">Facilities</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((m: any, idx: number) => {
                        const roles = (m.role_codes || [])
                          .map((c: string) => ROLE_LABELS[String(c)] || `Code ${c}`)
                          .join(', ');
                        const memberCcns: string[] = m.ccn_list || [];
                        const scopedFacilities = isStateScoped && facilityCards.length > 0
                          ? memberCcns.filter((c: string) => facilityCards.some(f => f.ccn === c))
                          : memberCcns;
                        const ownerKey = `${m.owner_name}-${m.tier}-${idx}`;
                        const isExpanded = expandedOwner === ownerKey;
                        return (
                          <React.Fragment key={idx}>
                            <TableRow
                              onClick={() => setExpandedOwner(isExpanded ? null : ownerKey)}
                              sx={{ '&:hover': { backgroundColor: 'grey.50' }, cursor: 'pointer' }}
                            >
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {isExpanded ? <KeyboardArrowDown sx={{ fontSize: 18, color: 'text.secondary' }} /> : <KeyboardArrowRight sx={{ fontSize: 18, color: 'text.secondary' }} />}
                                  {m.owner_name}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={m.type_owner === 'I' ? 'Individual' : 'Organization'}
                                  size="small"
                                  variant="outlined"
                                  color={m.type_owner === 'I' ? 'info' : 'default'}
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{roles}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{scopedFacilities.length} / {facilityCards.length}</TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ py: 0, px: 0, borderBottom: 'none' }}>
                                  <Collapse in={isExpanded}>
                                    <Box sx={{ pl: 6, pr: 2, py: 1, backgroundColor: 'grey.50' }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.disabled', letterSpacing: 0.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' } }}>
                                            <TableCell>Facility</TableCell>
                                            <TableCell>CCN</TableCell>
                                            <TableCell>Role</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {scopedFacilities.map((ccn: string) => {
                                            const fac = facilityCards.find(f => f.ccn === ccn);
                                            return (
                                              <TableRow key={ccn} sx={{ '& td': { py: 0.5, borderBottom: '1px solid', borderColor: 'grey.200' } }}>
                                                <TableCell sx={{ fontSize: '0.8rem' }}>{fac ? fac.name : ccn}</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{ccn}</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{roles}</TableCell>
                                              </TableRow>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );

            return (
              <Box sx={{ px: 3, py: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  All of the individuals and organizations listed below have ownership interests in at least some of these affiliated facilities.
                </Typography>
                {renderTierSection(
                  'Owners',
                  '5%+ direct and indirect ownership, general and limited partnership interests',
                  tier1
                )}
                {renderTierSection(
                  'Officers, Directors & Managing Employees',
                  'Corporate officers, directors, managing employees, trustees, and authorized designated persons',
                  tier2
                )}
                {renderTierSection(
                  'Mortgage & Security Interests',
                  '5%+ mortgage and security interest holders',
                  tier3
                )}
                {renderTierSection(
                  'Other',
                  'Other associated parties',
                  tier4
                )}
              </Box>
            );
          })()}

        </Paper>
      </Box>

      {/* Risk Score Modal */}
      <Dialog open={riskModalOpen} onClose={() => setRiskModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Average Risk Score Breakdown</Typography>
          <IconButton onClick={() => setRiskModalOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {avgRiskScore != null && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, p: 2, backgroundColor: `${avgRiskColor}0A`, borderRadius: 2, border: '1px solid', borderColor: `${avgRiskColor}30` }}>
                <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: avgRiskColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.4rem', flexShrink: 0, boxShadow: `0 4px 12px ${avgRiskColor}44` }}>
                  {Math.round(avgRiskScore)}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: avgRiskColor, textTransform: 'capitalize' }}>
                    {avgRiskLevel} Risk
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average across {facsWithRisk.length} {facsWithRisk.length === 1 ? 'facility' : 'facilities'}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Component Factors (Avg across facilities)</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {OWNER_RISK_FACTORS.map((factor, idx) => {
                  const factorNum = idx + 1;
                  const avg = avgFactorScores[factorNum];
                  const factorScore = avg ? Math.round(avg.total / avg.count * 10) / 10 : null;
                  const barValue = factorScore != null ? Math.min(factorScore, 100) : 0;
                  const barColor = barValue >= 75 ? '#d32f2f' : barValue >= 50 ? '#ed6c02' : barValue >= 25 ? '#fbc02d' : '#2e7d32';

                  return (
                    <Box key={factor.key}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Box sx={{ flex: 1, mr: 2 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {factor.label}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({factor.weight}% weight)
                            </Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}>
                            {factor.description}
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: barColor, lineHeight: 1.2, flexShrink: 0 }}>
                          {factorScore != null ? Math.round(factorScore) : '-'}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={barValue}
                        sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200', '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: barColor } }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
